/**
 * ModuleFrame.jsx
 * Shared pieces for every module of the MbuziPlus Course by Imfuyo Academy:
 * the Imfuyo MbuziPlus logo, "In this module" summary (a summary only), the
 * "How MbuziPlus handles this" panel, and the sources and acknowledgements.
 */
import { MODULES, PARTS, LOGO, SUMMARIES } from "./courseMap";

const byId = (id) => MODULES.find((m) => m.id === id);

export function ModuleIntro({ moduleId, isDark, onStart }) {
  const m = byId(moduleId);
  const s = SUMMARIES[moduleId];
  if (!m) return null;
  const part = PARTS.find((p) => p.no === m.part);
  const ink = isDark ? "#f0f7f0" : "#0d1a0f";
  const sub = isDark ? "#9fb7a2" : "#4b6a50";
  const card = isDark ? "#13231a" : "#ffffff";
  const line = isDark ? "#223328" : "#d8edd8";
  const soft = isDark ? "#16301c" : "#eef6ea";
  return (
    <section aria-label={`Module ${m.no} overview`} style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        <img src={LOGO} alt="Imfuyo MbuziPlus" style={{ width: 58, height: 58, objectFit: "contain", borderRadius: 10, background: "#fff", border: `1px solid ${line}` }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#3E7A32" }}>
            Module {m.no} of {MODULES.length} · Part {m.part}: {part.name}
          </div>
          <div style={{ fontSize: 13, color: sub, marginTop: 2 }}>{m.hours} hours of study · MbuziPlus Course by Imfuyo Academy</div>
        </div>
      </div>

      {s && (
        <div style={{ background: card, border: `1px solid ${line}`, borderRadius: 16, padding: "18px 20px", marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#3E7A32", marginBottom: 8 }}>In this module</div>
          <p style={{ margin: "0 0 12px", fontSize: 14.5, lineHeight: 1.7, color: ink }}>{s.summary}</p>
          <div style={{ fontSize: 13, fontWeight: 700, color: ink, marginBottom: 6 }}>By the end you will be able to:</div>
          <ul style={{ margin: "0 0 14px", paddingLeft: 20, color: ink, fontSize: 14, lineHeight: 1.65 }}>
            {s.outcomes.map((o) => <li key={o}>{o}</li>)}
          </ul>
          <div style={{ background: soft, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: sub, lineHeight: 1.5, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
            <span><b style={{ color: ink }}>This is a summary only.</b> You complete the module by reading every lesson and passing the quiz.</span>
            {onStart && <button onClick={onStart} style={{ border: "none", background: "#285020", color: "#fff", fontWeight: 700, fontSize: 13, borderRadius: 999, padding: "8px 16px", cursor: "pointer", fontFamily: "inherit" }}>Start the module</button>}
          </div>
        </div>
      )}

      <div style={{ background: isDark ? "#14231a" : "#fff7ee", border: `1px solid ${isDark ? "#3a2c1a" : "#f3d3b3"}`, borderRadius: 14, padding: "12px 16px", display: "flex", gap: 12, alignItems: "flex-start" }}>
        <img src={LOGO} alt="" style={{ width: 34, height: 34, objectFit: "contain", borderRadius: 8, background: "#fff", flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#D9601A" }}>How MbuziPlus handles this</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: ink, marginTop: 3 }}>{m.mbuziplus}.</div>
        </div>
      </div>
    </section>
  );
}

export function SourcesPanel({ moduleId, isDark }) {
  const s = SUMMARIES[moduleId];
  if (!s || !s.sources) return null;
  const ink = isDark ? "#f0f7f0" : "#0d1a0f";
  const sub = isDark ? "#9fb7a2" : "#4b6a50";
  const line = isDark ? "#223328" : "#d8edd8";
  return (
    <section aria-label="Sources and acknowledgements" style={{ marginTop: 28, borderTop: `1px solid ${line}`, paddingTop: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#3E7A32", marginBottom: 8 }}>Sources and acknowledgements</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: ink }}>
        {s.sources.map((x) => (
          <li key={x.url || x.label}>{x.url ? <a href={x.url} target="_blank" rel="noreferrer" style={{ color: "#285020" }}>{x.label}</a> : <span>{x.label}</span>}</li>
        ))}
      </ul>
      <p style={{ margin: "10px 0 0", fontSize: 12, lineHeight: 1.6, color: sub }}>
        We thank these organisations for their published research and extension material, and our partner Billy's Natural Flavors Limited and its partners for sharing their Kenyan Dairy Goat Farmers Workbook and Trainers Guide. The lessons are written in our own words from these sources.
        MbuziPlus and the MbuziPlus Course are owned by Imfuyo Usajili Solutions Limited. All rights reserved.
      </p>
    </section>
  );
}
